import Axios_lib from 'axios';
import { apiConfig, createAxiosServiceInstance } from '../api';
import { AMLS_NS_IAD } from '../../pages/constants/globalConstants';
import { getLocalTimezone } from '../../util/timezone';
import { uniqueId } from 'lodash';
import {
  downloadFile,
  showFileDownloadFailedNotification,
  showFileDownloadSucessNotification,
  showFileDownloadingNotification
} from '../../pages/cm/utils/common';
import { notification } from 'antd';

const axios = createAxiosServiceInstance();
const screeningBaseURL = `${apiConfig.serviceApi}${AMLS_NS_IAD}`;

export const fetchSegmentsList = async () => {
  const params = {
    sourceId: 7,
    offset: 0,
    limit: 500
  };

  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/runInstance/getRunInstanceForChampion`;

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error(res?.data?.message ?? 'Failed to fetch segments data');
    }
    return res.data;
  });
};

export const proceedScreening = async ({ screeningData, isBulkScreening }) => {
  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/realtime/screening`;
  const payload = {
    format: 'tt_json',
    payload: screeningData
  };

  return axios.post(url, payload).then((res) => {
    if (res.status !== 200) {
      throw new Error(res?.data?.message ?? 'Failed to proceed screening');
    }
    return res.data;
  });
};

export const downloadTemplate = () => {
  const uuid = uniqueId();
  const fileName = 'Bulk Upload Template.csv';

  showFileDownloadingNotification({
    key: uuid,
    fileName
  });

  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/onDemandScreening/sampleTemplate`;
  axios({
    url: url,
    method: 'GET',
    responseType: 'blob'
  }).then((res) => {
    if (res.status === 200) {
      showFileDownloadingNotification({ key: uuid, fileName });
      try {
        downloadFile(res.data, fileName);
        showFileDownloadSucessNotification({ key: uuid, fileName });
      } catch (e) {
        showFileDownloadFailedNotification({ key: uuid, fileName });
      }
    } else {
      showFileDownloadFailedNotification({ key: uuid, fileName });
    }
  });
};

let source;
export const uploadFile = async (file, onProgress) => {
  if (source) {
    source.cancel();
  }
  const CancelToken = Axios_lib.CancelToken;
  source = CancelToken.source();
  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/onDemandScreening/bulkUpload`;
  const config = {
    cancelToken: source.token,
    onUploadProgress: ({ loaded, total }) =>
      onProgress(Math.round((loaded * 80) / total))
  };

  return axios.post(url, file, config).then((res) => {
    if (res.status !== 200) {
      throw new Error(
        res?.data?.msg ?? res?.data?.message ?? 'Failed to upload the file'
      );
    }
    return res.data;
  });
};

let sourceDownload;

export async function cancelODNSHistoryCsvDownload() {
  if (sourceDownload) {
    await sourceDownload.cancel();
  }
  return true;
}

export async function fetchODNSHistoryCsvDownload({
  alertIds = null, //optional param
  fromDate,
  toDate
}) {
  if (sourceDownload) {
    sourceDownload.cancel();
  }

  const CancelToken = Axios_lib.CancelToken;
  sourceDownload = CancelToken.source();

  const url = `${screeningBaseURL}/onDemandScreening/download`;

  const headers = {
    'Content-Type': 'application/json',
    'Content-Disposition': 'attachment;filename=history.csv',
    'Access-Control-Expose-Headers': 'Content-Type, Content-Disposition',
    'Time-Zone': getLocalTimezone()
  };

  // Validate input parameters
  if (!fromDate || !toDate) {
    console.error('Invalid input parameters, required FromD ate and To Date');
    return false; // Invalid parameters
  }

  const params = {
    from: fromDate.clone().startOf('day').utc().valueOf(),
    to: toDate.clone().endOf('day').utc().valueOf()
  };

  if (alertIds) {
    params['alertIds'] = alertIds?.join(','); // Convert alertIds to a comma-separated string
  }

  try {
    const response = await axios.get(url, {
      headers: headers,
      params: params,
      responseType: 'blob', // Important for handling binary data like files
      cancelToken: sourceDownload.token
    });

    // Validate if the response contains data
    if (
      !response.data ||
      response?.data?.size === 0 ||
      response?.status !== 200
    ) {
      let error = 'An error occurred while downloading the CSV.';
      if (response) {
        const status = response.status;

        if (status === 204) {
          error = 'No records found for the given range';
          notification.error({
            description: error
          });
        } else {
          if (status === 400) {
            error = 'Bad Request: The server could not understand the request.';
          } else if (status === 500) {
            error = 'Something went wrong, please contact administrator';
          }

          notification.error({
            message: 'Error Downloading csv!',
            description: error
          });
        }
      }

      throw new Error(error);
    }
    // Create a Blob from the response data
    const blob = new Blob([response.data], {
      type: response.headers['content-type']
    });

    const base64String = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result.split(',')[1]);
      };
      reader.readAsDataURL(blob);
    });

    return base64String; // Success
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
}

let sourceScreening;
let sourceScreeningProgress;

export const proceedBulkScreening = async ({ jobId }) => {
  const CancelToken = Axios_lib.CancelToken;
  sourceScreening = CancelToken.source();

  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/onDemandScreening/bulkJob/${jobId}/doAction`;
  const payload = {
    action: 'execute'
  };

  return axios
    .post(url, payload, { cancelToken: sourceScreening.token })
    .then((res) => {
      if (res.status !== 200 && res.status !== 202) {
        throw new Error(
          res?.data?.message ?? 'Failed to proceed bulk screening'
        );
      }
      return res.data;
    });
};

export const stopScreening = async ({ jobId }) => {
  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/onDemandScreening/bulkJob/${jobId}/update`;
  const payload = {
    status: 'Cancelled'
  };

  return axios.put(url, payload).then((res) => {
    if (res.status !== 200 && res.status !== 202) {
      throw new Error(res?.data?.message ?? 'Failed to stop the screening');
    }

    //If the proceed screening api is running then cancel it.
    if (sourceScreening) {
      sourceScreening.cancel();
    }

    // If the progress api is running then cancel it.
    if (sourceScreeningProgress) {
      sourceScreeningProgress.cancel();
    }

    return res.data;
  });
};

export const getScreeningProgress = async ({ jobId }) => {
  const CancelToken = Axios_lib.CancelToken;
  sourceScreeningProgress = CancelToken.source();
  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/onDemandScreening/bulkJob/${jobId}`;

  return axios
    .get(url, { cancelToken: sourceScreeningProgress.token })
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(
          res?.data?.message ?? 'Failed to get the screening status'
        );
      }
      return res.data;
    });
};

export const fetchMatchingConfigs = async ({ runInstanceId }) => {
  const url = `${apiConfig.serviceApi}${AMLS_NS_IAD}/runInstance/${runInstanceId}/matchingConfigs`;

  return axios.get(url).then((res) => {
    if (res.status !== 200) {
      throw new Error(
        res?.data?.message ?? 'Failed to fetch Whitelist/Watchlist data'
      );
    }
    return res.data;
  });
};
